var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useMemo } from "react";
import { InputHelperText, useInput } from "react-admin";
import { Alert, Box, Stack, TextField, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { TimeZonePicker } from "./TimeZonePicker";
import { useTimeZoneService } from "./timeZoneService";
import { hasOffset, zonedLines } from "./ZonedDateTime";
var LOCAL_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;
/** The wall time and zone to edit: as entered, else the instant in `zone`; empty for none. */
export var editableOf = function (stored, zone, service) {
    if ((stored === null || stored === void 0 ? void 0 : stored.local) && stored.timezone) {
        return { local: stored.local.slice(0, 16), timezone: stored.timezone };
    }
    if ((stored === null || stored === void 0 ? void 0 : stored.scheduled_date) && hasOffset(stored.scheduled_date)) {
        return { local: service.instantToZoned(stored.scheduled_date, zone), timezone: zone };
    }
    return { local: "", timezone: zone };
};
/** The saved triple of a wall time in a zone; null while the wall time is incomplete. */
export var zonedValueOf = function (local, timezone, service) {
    return LOCAL_PATTERN.test(local) && timezone
        ? { scheduled_date: service.zonedToInstant(local, timezone).instant, local: local, timezone: timezone }
        : null;
};
/**
 * A date and time in a chosen zone: the wall time, the timezone picker, and a
 * preview in the row's zone (with its place) and in my time. A time that
 * doesn't exist (DST gap) or happens twice (overlap) is explained.
 */
export var ZonedDateTimeField = function (_a) {
    var local = _a.local, timezone = _a.timezone, onChange = _a.onChange, label = _a.label, zones = _a.zones, primary = _a.primary, place = _a.place, required = _a.required, disabled = _a.disabled, error = _a.error, helperText = _a.helperText;
    var t = useTranslation().t;
    var service = useTimeZoneService();
    var resolved = useMemo(function () {
        return LOCAL_PATTERN.test(local) && timezone ? service.zonedToInstant(local, timezone) : null;
    }, [local, timezone, service]);
    var instant = resolved ? new Date(resolved.instant) : undefined;
    var change = function (next) {
        return onChange(zonedValueOf(next.local, next.timezone, service), next);
    };
    // The row's zone (with its place), then my time unless it reads the same.
    var inZone = instant
        ? place
            ? service.formatPlaceTime(instant, timezone, place, service.text)
            : service.formatDateTimeZone(instant, timezone, service.text)
        : null;
    var mine = instant ? zonedLines(instant, timezone, service).mine : null;
    var dst = resolved ? service.zonedTimeNote(local, timezone, service.text) : undefined;
    return (_jsxs(Stack, { spacing: 1, sx: { width: "100%" }, children: [_jsxs(Stack, { direction: { xs: "column", sm: "row" }, spacing: 2, children: [_jsx(TextField, { type: "datetime-local", label: label, value: local, required: required, disabled: disabled, error: error, helperText: helperText, onChange: function (event) { return change({ local: event.target.value, timezone: timezone }); }, slotProps: { inputLabel: { shrink: true } }, sx: { flex: 1, minWidth: 0 } }), _jsx(Box, { sx: { flex: 1, minWidth: 0 }, children: _jsx(TimeZonePicker, { label: t("lifecycle.input.timezone"), value: timezone, zones: zones, primary: primary, at: instant, disabled: disabled, onChange: function (zone) { return change({ local: local, timezone: zone !== null && zone !== void 0 ? zone : timezone }); } }) })] }), inZone ? (_jsxs(Box, { "data-testid": "zoned-preview", sx: { bgcolor: "action.hover", borderRadius: 1, px: 1.5, py: 1 }, children: [_jsx(Typography, { variant: "body2", children: inZone }), mine ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: mine })) : null] })) : null, dst ? (_jsx(Alert, { severity: (resolved === null || resolved === void 0 ? void 0 : resolved.kind) === "gap" ? "warning" : "info", children: dst })) : null] }));
};
/** The react-admin input of a zoned date and time; it saves `{scheduled_date, local, timezone}`. */
export var ZonedDateTimeInput = function (_a) {
    var _b;
    var source = _a.source, defaultZone = _a.defaultZone, validate = _a.validate, helperText = _a.helperText, props = __rest(_a, ["source", "defaultZone", "validate", "helperText"]);
    var service = useTimeZoneService();
    var _c = useInput({ source: source, validate: validate }), field = _c.field, fieldState = _c.fieldState;
    var stored = field.value;
    var _d = React.useState(function () { return editableOf(stored, defaultZone, service); }), edited = _d[0], setEdited = _d[1];
    var _e = React.useState(false), touched = _e[0], setTouched = _e[1];
    // The last value this input saved: a different one came from outside (a reset).
    var emitted = React.useRef(undefined);
    React.useEffect(function () {
        var _a;
        if (touched && JSON.stringify(stored !== null && stored !== void 0 ? stored : null) !== JSON.stringify((_a = emitted.current) !== null && _a !== void 0 ? _a : null)) {
            // The form was reset (or set) from outside: show what it holds now.
            setTouched(false);
            return;
        }
        if (touched)
            return;
        // Until the time is edited here, it follows the record and the row's zone,
        // which may load after the form.
        var next = editableOf(stored, defaultZone, service);
        setEdited(function (current) {
            return current.local === next.local && current.timezone === next.timezone ? current : next;
        });
    }, [touched, stored, defaultZone, service]);
    return (_jsx(ZonedDateTimeField, __assign({}, props, { local: edited.local, timezone: edited.timezone, error: fieldState.invalid, 
        // react-admin encodes validation errors; its helper text translates them.
        helperText: ((_b = fieldState.error) === null || _b === void 0 ? void 0 : _b.message) ? (_jsx(InputHelperText, { error: fieldState.error.message, helperText: helperText })) : (helperText), onChange: function (value, next) {
            setTouched(true);
            setEdited(next);
            emitted.current = value;
            field.onChange(value);
        } })));
};
